import React, { useMemo } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Link } from "@material-ui/core";
import { useSelector } from "react-redux";

import { accountNames } from "../../../../../constants";
import { useIconsByAccount } from "../../../../../utils/useIconsByAccount";

import { ICONS, Icon } from "../../../../../components/Icons/Icon";
import { ContainedButton } from "../../../../../components/Buttons/ContainedButton";
import { getAccountType, getIsIslamicBanking } from "../../../../../store/selectors/appConfig";

import { useStyles } from "./styled";

export const rakValuePackagePlusName = "RAKvalue SME PLUS";

const getUrlReadMore = (urls, islamicBanking, value) => {
  const {
    rakValuePlusIslamicReadMoreUrl,
    rakValueMaxIslamicReadMoreUrl,
    rakValuePlusReadMoreUrl,
    rakValueMaxReadMoreUrl
  } = urls;

  const isValueTypeHavePlusUrl = value.includes("PLUS");

  if (islamicBanking) {
    return isValueTypeHavePlusUrl ? rakValuePlusIslamicReadMoreUrl : rakValueMaxIslamicReadMoreUrl;
  }
  return isValueTypeHavePlusUrl ? rakValuePlusReadMoreUrl : rakValueMaxReadMoreUrl;
};

export const ExpandedDetailedOptionsCard = ({
  optionList,
  cost,
  value,
  className,
  buttonLabel,
  selectService,
  handleClick,
  disabled,
  id,
  readMoreUrls,
  isRAKstarter
}) => {
  const accountType = useSelector(getAccountType);
  const islamicBanking = useSelector(getIsIslamicBanking);

  const classes = useStyles({ islamicBanking });
  const { plus: Plus, max: Max } = useIconsByAccount();
  const href = getUrlReadMore(readMoreUrls, islamicBanking, value);

  const showWomenSpecialOfferBanner = useMemo(() => {
    return isRAKstarter && value === rakValuePackagePlusName;
  }, [isRAKstarter, value]);

  return (
    <div className={cx(classes.root, isRAKstarter ? "spclOfferBannerStyle" : "", className)}>
      <div className={classes.spclPackageListTitleWrapper}>
        {showWomenSpecialOfferBanner && (
          <div className={classes.spclPackageListTitleDesc}>
            Recommended for female entrepreneurs{" "}
            <sup className={classes.spclPackageSupscript}>†</sup>
          </div>
        )}
      </div>
      <div className={cx(classes.title, isRAKstarter ? "spclOfferTitleStyle" : "")}>
        <div className={classes.icon}>
          {value === rakValuePackagePlusName ? (
            <Plus width={80} height={80} alt="rak-plus" />
          ) : (
            <Max width={80} height={80} alt="rak-max" />
          )}
        </div>
        <div className={classes.name}>{value}</div>
      </div>
      {optionList && (
        <ul className={classes.options}>
          {optionList.map(option => (
            <li key={option.text}>
              <Icon className={classes.listIcon} name={ICONS.checkOutline} alt="check" />
              <div
                className={cx("text", classes.indent)}
                dangerouslySetInnerHTML={{
                  __html: islamicBanking && option.textIslamic ? option.textIslamic : option.text
                }}
              />
              {option.items && (
                <ul className={classes.nestedOptions}>
                  {option.items.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className={classes.linkWrapper}>
        <Link href={href} className={classes.link} target="_blank">
          Read more
        </Link>
      </div>
      <div className={classes.cost}>
        <span className={"aed-currency"}>AED </span>&nbsp;
        {cost} /month
        {showWomenSpecialOfferBanner && <sup className={classes.spclPackageSupscript}>†</sup>}
      </div>
      <div className={classes.upgrade}>
        {selectService ? (
          <ContainedButton
            disabled={disabled}
            label={buttonLabel}
            className={classes.button}
            handleClick={() => handleClick(id)}
          />
        ) : accountType === accountNames.starter && value === rakValuePackagePlusName ? (
          "Included in RAKstarter"
        ) : (
          "Upgrade available"
        )}
      </div>
    </div>
  );
};

ExpandedDetailedOptionsCard.propTypes = {
  optionList: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      textIslamic: PropTypes.string,
      items: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.string.isRequired,
  className: PropTypes.string,
  buttonLabel: PropTypes.string,
  selectService: PropTypes.bool,
  handleClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  readMoreUrls: PropTypes.shape({
    rakValuePlusIslamicReadMoreUrl: PropTypes.string,
    rakValueMaxIslamicReadMoreUrl: PropTypes.string,
    rakValuePlusReadMoreUrl: PropTypes.string,
    rakValueMaxReadMoreUrl: PropTypes.string
  }),
  isRAKstarter: PropTypes.bool
};
